import { useState, useMemo, useCallback } from 'react';
import { AlertCircle, CheckCircle2, Download, Loader2 } from 'lucide-react';
import type {
  ClinicCapabilityDiffDto,
  ClinicMatrixCapabilityExecuteRequest,
  ClinicCapabilityUpdateItem,
  CapabilityFieldDiff,
  ResolvedFieldChoice,
} from '../../types/cutover';
import './CapabilityDiffPanel.css';

type FieldChoice = 'db' | 'spreadsheet' | 'skip';

/** Map: clinicId -> fieldName -> choice */
type ChoicesMap = Map<number, Map<string, FieldChoice>>;

interface CapabilityDiffPanelProps {
  diffs: ClinicCapabilityDiffDto[];
  onExecute: (request: ClinicMatrixCapabilityExecuteRequest) => Promise<void> | void;
  /** Optional callback that also receives all resolved "spreadsheet" field choices.
   *  Used by Clinic Codes handler to build junction table updates from non-column fields. */
  onExecuteWithChoices?: (request: ClinicMatrixCapabilityExecuteRequest, resolvedChoices: ResolvedFieldChoice[]) => Promise<void> | void;
  isExecuting: boolean;
  title?: string;
}

/** Return a display-friendly label for a capability field name */
function fieldLabel(fieldName: string): string {
  switch (fieldName) {
    // Legacy column names (pre SLV2-3301 previews restored from session)
    case 'HasPAT': return 'Has PAT';
    case 'FitTestType': return 'Fit Test Type';
    case 'HasXRay': return 'Has X-Ray';
    // Current targets — one home per attribute (SLV2-3301)
    case 'MaskTypes': return 'Mask Types';
    case 'Masks': return 'Masks (capability)';
    case 'PAT': return 'Physical Ability Test (procedure)';
    case 'FitTest': return 'Fit Test (procedures)';
    case 'QualFit': return 'Qualitative Fit Test (procedure)';
    case 'QuantFit': return 'Quantitative Fit Test (procedure)';
    case 'XRay': return 'X-ray On Site (capability)';
    case 'NIOSH': return 'NIOSH Certified Technician (capability)';
    case 'eScreen': return 'eScreen (chain-of-custody vendor)';
    case 'Formfox': return 'FormFox (chain-of-custody vendor)';
    case 'Audiogram': return 'Audiogram (procedure)';
    case 'PFT': return 'PFT / Spirometry (procedure)';
    case 'Venipuncture': return 'Venipuncture (procedure)';
    case 'EKG': return 'EKG (procedure)';
    case 'DOTPhysical': return 'DOT Physical (procedure)';
    case 'StdPhysical': return 'Standard / Basic Physical (procedure)';
    case 'BAT': return 'Breath Alcohol Tests, DOT + non-DOT (procedures)';
    case 'DrugScreen': return 'Drug Screen Collections, all types (procedures)';
    case 'EmergencyRoom': return 'Emergency Room (category)';
    default: return fieldName;
  }
}

/** Display a value in the diff table (handle null/empty) */
function displayValue(value: string | null): { text: string; isEmpty: boolean } {
  if (value === null || value === '') {
    return { text: '(empty)', isEmpty: true };
  }
  return { text: value, isEmpty: false };
}

/** Build default choices from diffs */
function buildDefaultChoices(diffs: ClinicCapabilityDiffDto[]): ChoicesMap {
  const map: ChoicesMap = new Map();
  for (const diff of diffs) {
    const fieldMap = new Map<string, FieldChoice>();
    for (const field of diff.fields) {
      if (!field.isDifferent) {
        // Values match — no choice needed, we won't render a radio for this
        continue;
      }
      if (field.isAutoAccepted) {
        // DB was blank, spreadsheet has a value — auto-accept
        fieldMap.set(field.fieldName, 'spreadsheet');
      } else if (field.isUnrecognized) {
        fieldMap.set(field.fieldName, 'skip');
      } else if (diff.hasMultiRowConflict && isFieldMultiRowConflict(field)) {
        fieldMap.set(field.fieldName, 'skip');
      } else {
        // SLV2-3846 (Jared, cutover safety): a difference that would CHANGE existing data starts on
        // "keep the database value"; nothing changes unless someone picks the spreadsheet value.
        fieldMap.set(field.fieldName, 'db');
      }
    }
    map.set(diff.clinicId, fieldMap);
  }
  return map;
}

/** Check if a field is the one with multi-row conflict (spreadsheetValue will indicate conflict) */
function isFieldMultiRowConflict(field: CapabilityFieldDiff): boolean {
  // The API flags multi-row conflict at the clinic level. If a field has
  // isUnrecognized=false and isDifferent=true but the clinic hasMultiRowConflict,
  // we check if the spreadsheetValue looks like a conflict marker.
  // For now, treat fields that are different on a multi-row-conflict clinic as
  // potentially conflicted if the raw value differs from the normalized value
  // (i.e., multiple rows gave different values that couldn't be reconciled).
  return field.spreadsheetValue === null && field.rawSpreadsheetValue !== null;
}

/** Convert a string value to a boolean for HasPAT/HasXRay fields */
function toBoolOrNull(value: string | null): boolean | null {
  if (value === null || value === '') return null;
  const lower = value.toLowerCase();
  if (lower === 'yes' || lower === 'true' || lower === '1') return true;
  if (lower === 'no' || lower === 'false' || lower === '0') return false;
  return null;
}

export default function CapabilityDiffPanel({
  diffs,
  onExecute,
  onExecuteWithChoices,
  isExecuting,
  title = 'Capability Changes',
}: Readonly<CapabilityDiffPanelProps>) {
  const [choices, setChoices] = useState<ChoicesMap>(() => buildDefaultChoices(diffs));
  const [filter, setFilter] = useState<'all' | 'conflicts' | 'autoAccepted'>('all');
  const [autoAcceptApplied, setAutoAcceptApplied] = useState(false);

  // Counts
  const unrecognizedCount = useMemo(
    () => diffs.reduce((sum, d) => sum + d.fields.filter((f) => f.isUnrecognized).length, 0),
    [diffs],
  );

  const multiRowConflictCount = useMemo(
    () => diffs.filter((d) => d.hasMultiRowConflict).length,
    [diffs],
  );

  const autoAcceptedClinicCount = useMemo(
    () => diffs.filter((d) => d.hasAutoAcceptedOnly).length,
    [diffs],
  );

  // Set a single field choice
  const setFieldChoice = useCallback(
    (clinicId: number, fieldName: string, choice: FieldChoice) => {
      setChoices((prev) => {
        const next = new Map(prev);
        const fieldMap = new Map(next.get(clinicId) ?? new Map());
        fieldMap.set(fieldName, choice);
        next.set(clinicId, fieldMap);
        return next;
      });
    },
    [],
  );

  // Bulk: select all DB
  const selectAllDb = useCallback(() => {
    setChoices((prev) => {
      const next = new Map(prev);
      for (const diff of diffs) {
        const fieldMap = new Map(next.get(diff.clinicId) ?? new Map());
        for (const field of diff.fields) {
          if (field.isDifferent) {
            fieldMap.set(field.fieldName, 'db');
          }
        }
        next.set(diff.clinicId, fieldMap);
      }
      return next;
    });
  }, [diffs]);

  // Bulk: select all spreadsheet (only where allowed)
  const selectAllSpreadsheet = useCallback(() => {
    setChoices((prev) => {
      const next = new Map(prev);
      for (const diff of diffs) {
        const fieldMap = new Map(next.get(diff.clinicId) ?? new Map());
        for (const field of diff.fields) {
          if (!field.isDifferent) continue;
          if (field.isUnrecognized) continue;
          if (diff.hasMultiRowConflict && isFieldMultiRowConflict(field)) continue;
          fieldMap.set(field.fieldName, 'spreadsheet');
        }
        next.set(diff.clinicId, fieldMap);
      }
      return next;
    });
  }, [diffs]);

  // Count clinics that will actually have updates applied
  const appliedClinicCount = useMemo(() => {
    let count = 0;
    for (const diff of diffs) {
      const fieldMap = choices.get(diff.clinicId);
      if (!fieldMap) continue;
      const hasSpreadsheetChoice = Array.from(fieldMap.values()).some((c) => c === 'spreadsheet');
      if (hasSpreadsheetChoice) count++;
    }
    return count;
  }, [diffs, choices]);

  // Build execute request
  const handleApply = useCallback(() => {
    const items: ClinicCapabilityUpdateItem[] = [];
    const resolvedChoices: ResolvedFieldChoice[] = [];

    for (const diff of diffs) {
      const fieldMap = choices.get(diff.clinicId);
      if (!fieldMap) continue;

      // Check if any field has a "spreadsheet" choice
      const hasAnyUpdate = Array.from(fieldMap.values()).some((c) => c === 'spreadsheet');
      if (!hasAnyUpdate) continue;

      const item: ClinicCapabilityUpdateItem = {
        clinicId: diff.clinicId,
        hasPAT: null,
        fitTestType: null,
        maskTypes: null,
        hasXRay: null,
        setHasPAT: false,
        setFitTestType: false,
        setMaskTypes: false,
        setHasXRay: false,
      };

      for (const field of diff.fields) {
        const choice = fieldMap.get(field.fieldName);
        if (choice !== 'spreadsheet') continue;

        // Collect all resolved "spreadsheet" choices for the parent
        resolvedChoices.push({
          clinicId: diff.clinicId,
          fieldName: field.fieldName,
          spreadsheetValue: field.spreadsheetValue,
          // No default: a field without server-resolved target metadata must fail loudly in the
          // executor rather than be treated as a column write (PR #94 review).
          targetKind: field.targetKind,
          idsToAdd: field.idsToAdd ?? [],
          idsToRemove: field.idsToRemove ?? [],
        });

        switch (field.fieldName) {
          case 'HasPAT':
            item.setHasPAT = true;
            item.hasPAT = toBoolOrNull(field.spreadsheetValue);
            break;
          case 'FitTestType':
            item.setFitTestType = true;
            item.fitTestType = field.spreadsheetValue;
            break;
          case 'MaskTypes':
            item.setMaskTypes = true;
            item.maskTypes = field.spreadsheetValue;
            break;
          case 'HasXRay':
            item.setHasXRay = true;
            item.hasXRay = toBoolOrNull(field.spreadsheetValue);
            break;
        }
      }

      items.push(item);
    }

    const request: ClinicMatrixCapabilityExecuteRequest = { items };

    if (onExecuteWithChoices) {
      onExecuteWithChoices(request, resolvedChoices);
    } else {
      onExecute(request);
    }
  }, [diffs, choices, onExecute, onExecuteWithChoices]);

  // Count clinics with conflicts (not auto-accepted-only)
  const conflictClinicCount = useMemo(
    () => diffs.filter((d) => !d.hasAutoAcceptedOnly && d.fields.some((f) => f.isDifferent && !f.isAutoAccepted)).length,
    [diffs],
  );

  // Filtered diffs based on active filter toggle
  const filteredDiffs = useMemo(() => {
    switch (filter) {
      case 'conflicts':
        return diffs.filter((d) => !d.hasAutoAcceptedOnly && d.fields.some((f) => f.isDifferent && !f.isAutoAccepted));
      case 'autoAccepted':
        return diffs.filter((d) => d.hasAutoAcceptedOnly);
      default:
        return diffs;
    }
  }, [diffs, filter]);

  // Bulk-apply all auto-accepted diffs
  const handleApplyAutoAccepted = useCallback(async () => {
    const autoAcceptedDiffs = diffs.filter((d) => d.hasAutoAcceptedOnly);
    if (autoAcceptedDiffs.length === 0) return;

    const confirmed = window.confirm(
      `Apply ${autoAcceptedDiffs.length} changes where DB was blank and spreadsheet has a value?`,
    );
    if (!confirmed) return;

    const autoChoices = buildDefaultChoices(autoAcceptedDiffs);
    const items: ClinicCapabilityUpdateItem[] = [];
    const resolvedChoices: ResolvedFieldChoice[] = [];

    for (const diff of autoAcceptedDiffs) {
      const fieldMap = autoChoices.get(diff.clinicId);
      if (!fieldMap) continue;

      const hasAnyUpdate = Array.from(fieldMap.values()).some((c) => c === 'spreadsheet');
      if (!hasAnyUpdate) continue;

      const item: ClinicCapabilityUpdateItem = {
        clinicId: diff.clinicId,
        hasPAT: null,
        fitTestType: null,
        maskTypes: null,
        hasXRay: null,
        setHasPAT: false,
        setFitTestType: false,
        setMaskTypes: false,
        setHasXRay: false,
      };

      for (const field of diff.fields) {
        const choice = fieldMap.get(field.fieldName);
        if (choice !== 'spreadsheet') continue;

        resolvedChoices.push({
          clinicId: diff.clinicId,
          fieldName: field.fieldName,
          spreadsheetValue: field.spreadsheetValue,
          // No default: a field without server-resolved target metadata must fail loudly in the
          // executor rather than be treated as a column write (PR #94 review).
          targetKind: field.targetKind,
          idsToAdd: field.idsToAdd ?? [],
          idsToRemove: field.idsToRemove ?? [],
        });

        switch (field.fieldName) {
          case 'HasPAT':
            item.setHasPAT = true;
            item.hasPAT = toBoolOrNull(field.spreadsheetValue);
            break;
          case 'FitTestType':
            item.setFitTestType = true;
            item.fitTestType = field.spreadsheetValue;
            break;
          case 'MaskTypes':
            item.setMaskTypes = true;
            item.maskTypes = field.spreadsheetValue;
            break;
          case 'HasXRay':
            item.setHasXRay = true;
            item.hasXRay = toBoolOrNull(field.spreadsheetValue);
            break;
        }
      }

      items.push(item);
    }

    const request: ClinicMatrixCapabilityExecuteRequest = { items };

    try {
      if (onExecuteWithChoices) {
        await onExecuteWithChoices(request, resolvedChoices);
      } else {
        await onExecute(request);
      }
      setAutoAcceptApplied(true);
    } catch {
      // Execute failed — leave autoAcceptApplied as false so the button remains enabled
    }
  }, [diffs, onExecute, onExecuteWithChoices]);

  // Download diffs as CSV
  const downloadCsv = useCallback(() => {
    const escapeCsv = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const header = 'Clinic Code,Clinic Name,Field,DB Value,Spreadsheet Value,Auto-Accepted,Source Rows';
    const rows: string[] = [header];

    for (const diff of diffs) {
      for (const field of diff.fields) {
        if (!field.isDifferent) continue;
        rows.push(
          [
            escapeCsv(diff.clinicCode ?? ''),
            escapeCsv(diff.clinicName),
            escapeCsv(fieldLabel(field.fieldName)),
            escapeCsv(field.dbValue ?? ''),
            escapeCsv(field.spreadsheetValue ?? ''),
            field.isAutoAccepted ? 'Yes' : 'No',
            escapeCsv(diff.sourceRowNumbers.join('; ')),
          ].join(','),
        );
      }
    }

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `capability-diffs-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [diffs]);

  if (diffs.length === 0) return null;

  return (
    <div className="cdp-container">
      {/* Header */}
      <div className="cdp-header">
        <div className="cdp-header-title-row">
          <h3>{title} ({diffs.length} clinic{diffs.length !== 1 ? 's' : ''} with differences)</h3>
          <button type="button" className="cdp-bulk-btn" onClick={downloadCsv}>
            <Download size={12} />
            Download Diffs (CSV)
          </button>
        </div>
        <div className="cdp-summary-badges">
          {autoAcceptedClinicCount > 0 && (
            <span className="cdp-summary-badge auto-accepted">
              <CheckCircle2 size={12} />
              {autoAcceptedClinicCount} auto-accepted (DB was empty)
            </span>
          )}
          {unrecognizedCount > 0 && (
            <span className="cdp-summary-badge unrecognized">
              <AlertCircle size={12} />
              {unrecognizedCount} unrecognized value{unrecognizedCount !== 1 ? 's' : ''}
            </span>
          )}
          {multiRowConflictCount > 0 && (
            <span className="cdp-summary-badge multi-row">
              <AlertCircle size={12} />
              {multiRowConflictCount} multi-row conflict{multiRowConflictCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Filter toggles */}
      <div className="cdp-filter-toggles">
        <button
          type="button"
          className={`cdp-filter-btn ${filter === 'all' ? 'cdp-filter-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({diffs.length})
        </button>
        <button
          type="button"
          className={`cdp-filter-btn ${filter === 'conflicts' ? 'cdp-filter-active' : ''}`}
          onClick={() => setFilter('conflicts')}
        >
          Conflicts Only ({conflictClinicCount})
        </button>
        <button
          type="button"
          className={`cdp-filter-btn ${filter === 'autoAccepted' ? 'cdp-filter-active' : ''}`}
          onClick={() => setFilter('autoAccepted')}
        >
          Auto-Accepted ({autoAcceptedClinicCount})
        </button>
      </div>

      {/* Bulk actions */}
      <div className="cdp-bulk-actions">
        <button type="button" className="cdp-bulk-btn" onClick={selectAllDb}>
          Select All: Keep DB
        </button>
        <button type="button" className="cdp-bulk-btn" onClick={selectAllSpreadsheet}>
          Select All: Use Spreadsheet
        </button>
        {autoAcceptedClinicCount > 0 && (
          autoAcceptApplied ? (
            <span className="cdp-auto-accept-done">
              <CheckCircle2 size={14} />
              {autoAcceptedClinicCount} Auto-Accepted Applied
            </span>
          ) : (
            <button
              type="button"
              className="cdp-bulk-btn cdp-bulk-btn-auto"
              onClick={handleApplyAutoAccepted}
              disabled={isExecuting}
            >
              {isExecuting ? <Loader2 size={12} className="cdp-spin" /> : null}
              Apply {autoAcceptedClinicCount} Auto-Accepted
            </button>
          )
        )}
      </div>

      {/* Clinic cards */}
      {filteredDiffs.map((diff) => {
        const fieldMap = choices.get(diff.clinicId);
        const hasUnrecognized = diff.fields.some((f) => f.isUnrecognized);
        const cardClasses = [
          'cdp-clinic-card',
          hasUnrecognized ? 'has-unrecognized' : '',
          diff.hasMultiRowConflict ? 'has-multi-row' : '',
          diff.hasAutoAcceptedOnly ? 'auto-accepted-only' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={diff.clinicId} className={cardClasses}>
            <div className="cdp-clinic-header">
              <span className="cdp-clinic-name">
                {diff.clinicName}
                {diff.clinicCode && (
                  <span className="cdp-clinic-code"> ({diff.clinicCode})</span>
                )}
              </span>
              <span className="cdp-clinic-rows">
                Row{diff.sourceRowNumbers.length !== 1 ? 's' : ''}: {diff.sourceRowNumbers.join(', ')}
              </span>
            </div>

            <table className="cdp-field-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>DB Value</th>
                  <th>Spreadsheet</th>
                  <th>Choice</th>
                </tr>
              </thead>
              <tbody>
                {diff.fields.map((field) => {
                  const dbDisplay = displayValue(field.dbValue);
                  const ssDisplay = field.isUnrecognized
                    ? {
                        text: `??? (raw: "${field.rawSpreadsheetValue ?? ''}")`,
                        isEmpty: false,
                      }
                    : displayValue(field.spreadsheetValue);

                  const isMultiConflict =
                    diff.hasMultiRowConflict && isFieldMultiRowConflict(field) && field.isDifferent;

                  const rowClasses = [
                    'cdp-field-row',
                    field.isUnrecognized ? 'unrecognized' : '',
                    isMultiConflict ? 'multi-row-conflict' : '',
                    field.isAutoAccepted ? 'auto-accepted' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  // Determine if "Spreadsheet" radio option is allowed
                  const allowSpreadsheet =
                    field.isDifferent && !field.isUnrecognized && !isMultiConflict;

                  const currentChoice = fieldMap?.get(field.fieldName);

                  return (
                    <tr key={field.fieldName} className={rowClasses}>
                      <td className="cdp-field-name">{fieldLabel(field.fieldName)}</td>
                      <td className={`cdp-field-value ${dbDisplay.isEmpty ? 'empty' : ''}`}>
                        {dbDisplay.text}
                      </td>
                      <td
                        className={`cdp-field-value ${ssDisplay.isEmpty ? 'empty' : ''} ${field.isUnrecognized ? 'unrecognized-value' : ''}`}
                      >
                        {isMultiConflict ? 'CONFLICT' : ssDisplay.text}
                      </td>
                      <td className="cdp-field-choice">
                        {!field.isDifferent ? (
                          <span className="cdp-match-indicator">= no change</span>
                        ) : field.isAutoAccepted ? (
                          <span className="cdp-auto-accepted-indicator">
                            <CheckCircle2 size={12} />
                            Auto-accepted
                          </span>
                        ) : (
                          <div className="cdp-radio-group">
                            <label className="cdp-radio-label">
                              <input
                                type="radio"
                                name={`choice-${diff.clinicId}-${field.fieldName}`}
                                checked={currentChoice === 'db'}
                                onChange={() =>
                                  setFieldChoice(diff.clinicId, field.fieldName, 'db')
                                }
                              />
                              DB
                            </label>
                            {allowSpreadsheet && (
                              <label className="cdp-radio-label">
                                <input
                                  type="radio"
                                  name={`choice-${diff.clinicId}-${field.fieldName}`}
                                  checked={currentChoice === 'spreadsheet'}
                                  onChange={() =>
                                    setFieldChoice(diff.clinicId, field.fieldName, 'spreadsheet')
                                  }
                                />
                                Spreadsheet
                              </label>
                            )}
                            <label className="cdp-radio-label">
                              <input
                                type="radio"
                                name={`choice-${diff.clinicId}-${field.fieldName}`}
                                checked={currentChoice === 'skip'}
                                onChange={() =>
                                  setFieldChoice(diff.clinicId, field.fieldName, 'skip')
                                }
                              />
                              Skip
                            </label>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Warning messages below the table */}
            {diff.fields.some((f) => f.isUnrecognized) && (
              <div className="cdp-warning unrecognized-warning">
                <AlertCircle size={12} />
                Unrecognized value &mdash; only DB or Skip available
              </div>
            )}
            {diff.hasMultiRowConflict && (
              <>
                <div className="cdp-warning multi-row-warning">
                  <AlertCircle size={12} />
                  Multiple rows have different values
                </div>
                {diff.multiRowConflictNotes && (
                  <div className="cdp-multi-row-details">{diff.multiRowConflictNotes}</div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Action bar */}
      <div className="cdp-action-bar">
        <button
          type="button"
          className="cdp-apply-btn"
          onClick={handleApply}
          disabled={isExecuting || appliedClinicCount === 0}
        >
          {isExecuting ? <Loader2 size={14} className="cdp-spin" /> : null}
          Apply {appliedClinicCount} Capability Change{appliedClinicCount !== 1 ? 's' : ''}
        </button>
      </div>
    </div>
  );
}
